import {
  ChatbotMessageInstance,
  createChatbotConfig,
  createChatbotEvents,
  LineWebhookEvent,
} from '@play-co/replicant';
import { stateSchema } from '../State';
import { chatbotMessageTemplates } from './messageTemplates';
import messages from '../config/messages';
import scheduledActions from '../config/scheduledActions';
import * as Pet from '../pet';
import computedProperties from '../config/computedProperties';
import { scheduleStatusUpdateMessage } from '../petChatbot/petChatbot.scheduledActions';
import asyncGetters from '../config/asyncGetters';
import { scheduleEnergyRefilledMessage } from '../inventory/inventory.scheduledActions';
import * as Inventory from '../inventory';
import { getLocale } from '../getters/user';
import * as Fate from '../fate';
import { duration } from '../utils/duration';
import { scheduleLiveopsMessages } from './chatbot.util';
import { createReportableError } from '../utils/errors';
import { autoResponse } from './chatbot.ruleset';
import { Feature } from '../analytics';
import {
  trackChatbotResponseFailure,
  trackChatbotResponseSuccess,
} from './chatbot.analytics';
import { ReplyFlexMessageData } from './flex/reply';
import { MINI_APP_REDIRECT_URL } from '../utils/payload';
import {
  enrollInCompactRichMenuTest,
  relinkRichMenu,
} from './chatbot.modifiers';
import ab from '../ruleset/ab';
import {
  cancelPreEntryMessages,
  enrollPreEntryMessages,
} from './preEntry.modifiers';

const events = createChatbotEvents(stateSchema, {
  messages,
  scheduledActions,
  computedProperties,
  asyncGetters,
  ruleset: { abTests: ab.dynamicAB },
})({
  // 'follow' triggers onWebhook on subscribe; Replicant's createIfNotFound
  // auto-creates the user state so scheduled chatbot actions don't fail.
  // It also enrolls new subscribers in the compact rich menu test and relinks
  // the rich menu that LINE drops when a user blocks the OA.
  enabledWebhookEvents: ['message', 'postback', 'follow', 'unfollow'],

  /**
   * Answer on user messages in the Line chatbot
   */
  onWebhook: async (state, messageEvent, api) => {
    let userMessage = undefined;
    let payload = {};
    const start = Date.now();
    const event = messageEvent as LineWebhookEvent;
    const type = event.type;

    if (type === 'follow') {
      // Only a new follow enrolls; unblocking must not start a pre-entry sequence.
      if (!event.follow?.isUnblocked) {
        enrollPreEntryMessages(state, event.timestamp, api);
      }
      enrollInCompactRichMenuTest(state, api);
      relinkRichMenu(state, api);
      return;
    }

    // Remove the remaining pre-entry schedules on block.
    if (type === 'unfollow') {
      // Only never-entered Enabled users can have treatment schedules to cancel.
      if (
        state.loginCount !== 0 ||
        api.abTests.getBucketID(ab.tests.TEST_PRE_ENTRY_CHATBOT) !== 'enabled'
      ) {
        return;
      }

      cancelPreEntryMessages(api);
      return;
    }

    const replyToken = event['replyToken'] as string;
    const petName = Pet.getters.getPetName(state);
    const evolutions = Pet.getters.getCurrentEvolution(state);
    const locale = getLocale(state);

    // Process text message, if user send sticker usually they have keywords
    if (type === 'message') {
      userMessage = (event.message as { text?: string }).text?.trim();
      const keywords = (event.message as { keywords?: string[] }).keywords;
      if (!userMessage && keywords) {
        userMessage = keywords?.join(' ');
      }
    }

    // Process postback event
    if (type === 'postback') {
      const data = (event.postback as { data: string }).data;
      try {
        const postbackData: ReplyFlexMessageData = JSON.parse(data);
        userMessage = postbackData.message;
        payload = { selectedIndex: postbackData?.selectedIndex ?? 'unknown' };
      } catch (_) {
        // Handle previous postback data format
        userMessage = data;
      }

      // Get the full payload from the postback data
      try {
        const postbackData: { $key: string } = JSON.parse(data);
        const payloadString = await api.kvStore.get(postbackData.$key);
        const decodedPayload = payloadString ? JSON.parse(payloadString) : {};
        payload = { ...payload, ...decodedPayload };
      } catch (error) {
        payload = payload ?? {};
      }
    }

    // Bail if the user sent an empty message
    if (!userMessage) {
      return;
    }

    // Bail if there is no reply token
    if (!replyToken) {
      trackChatbotResponseFailure(
        { elapsed: Date.now() - start, error: 'No reply token' },
        api,
      );
      api.reportError(
        createReportableError(
          'warning',
          new Error('ChatbotWebHook: No reply token, ignore message'),
        ),
      );
      return;
    }

    // Force MiniApp for all auto-responses (consistent with getAlternativeWebIABBucket)
    const url = MINI_APP_REDIRECT_URL;

    // Default message in case something goes wrong
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let answers: ChatbotMessageInstance<any, any>[] = [
      chatbotMessageTemplates.petChat({
        args: {
          species: evolutions?.species ?? 'ball',
          petName,
          message: autoResponse[locale](url),
        },
        payload: {
          $channel: 'CHATBOT',
          feature: Feature.CHATBOT._,
          $subFeature: Feature.CHATBOT.WEBHOOK_AUTO_RESPONSE,
        },
      }),
    ];

    // Create message template with the pet's response
    for (const message of answers) {
      api.chatbot.sendLineMessage(state.id, message, {
        replyToken,
      });
    }

    // Track the response time
    trackChatbotResponseSuccess({ elapsed: Date.now() - start, locale }, api);
  },

  onGameEnd: async (state, data, api) => {
    const currentSpecies = Pet.getters.getEquippedPetSpecies(state);

    // Bail if player never opened the initial crate
    if (!currentSpecies) {
      api.scheduledActions.schedule.sendSpawnPetMessage({
        args: {},
        notificationId: 'spawnPetCTA',
        delayInMS: duration({ minutes: 15 }),
      });

      return;
    }

    const now = api.date.now();
    scheduleStatusUpdateMessage(
      {
        nextPetState: Fate.getters.getNextPetState(state, now),
        currentSpecies,
        now,
      },
      api,
    );

    // Check if energy is less than 40%.
    const scheduleEnergyRefilled =
      Inventory.getters.getEnergy(state, now) <
      Math.floor(0.4 * Inventory.getters.getMaxEnergy(state));

    // Energy is less than 40%. Schedule energy refilled message.
    if (scheduleEnergyRefilled) {
      scheduleEnergyRefilledMessage(
        state,
        Inventory.getters.getTimeToFullEnergy(state, now),
        getLocale(state),
        api,
      );
    }

    scheduleLiveopsMessages(state, api);
  },
});

const chatbot = createChatbotConfig(chatbotMessageTemplates, events);

export default chatbot;
